// ============================================================
// KI-Fach- & Akte-Assistent (DSGVO-Klasse GRÜN)
// Beantwortet arbeitsmedizinische Fach-/Rechtsfragen und fasst die
// (administrative) Firmen-Akte zusammen. Läuft über den in den
// Einstellungen gewählten KI-Anbieter (aiProvider), mit lokal
// hinterlegtem Key.
//
// DSGVO: NUR administrative/fachliche Inhalte an die KI — KEINE
// identifizierenden Patientendaten, keine Befunde/Diagnosen.
// (Mitarbeiter-Namen sind verschlüsselt und NICHT Teil der Akte.)
// ============================================================

import { aiChat, isAiConfigured } from './aiProvider';
import { loadAkte } from './akteService';
import { logAktion } from './auditLogService';
import type { MandantID } from '../types/branded';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

const SYSTEM_PROMPT = `Du bist der Fachassistent der AMed App für Dr. med. Klaus Karrer, MSc. (Arbeitsmedizin, AUVAsicher Steiermark, Bad Aussee / Schladming).

Fachgebiet: österreichische Arbeitsmedizin. Du kennst ASchG, VGÜ-Verordnung (BGBl. II Nr. 27/1997), AMR 4, AsVG, Präventionszeiten, ASA (§88 ASchG), Betriebsbegehung (§77 ASchG), biologische Arbeitsstoffe (§§49–52 ASchG), Bildschirmarbeit, Lärm/Vibration/Gefahrstoffe.

Regeln:
- Antworte präzise, praxisnah, auf Deutsch (österreichische Begriffe).
- Bei Rechts-/Normfragen nenne die maßgebliche Rechtsgrundlage (Paragraf/Verordnung).
- Gib KEINE individuelle medizinische Diagnose oder Therapieempfehlung für konkrete Patienten ab — du unterstützt die Praxisführung, nicht die Krankenbehandlung.
- Wenn dir Kontext (z.B. eine Firmen-Akte) mitgegeben wird, beziehe dich konkret darauf.
- Fasse dich angemessen kurz; nutze Aufzählungen wenn hilfreich.
- Bei Unsicherheit sag es klar und verweise auf die zu prüfende Quelle.`;

/** Freies Fach-Gespräch (Verlauf als Kontext). */
export async function chatAssistant(history: ChatMessage[]): Promise<string> {
  const antwort = await aiChat(SYSTEM_PROMPT, history);
  void logAktion('SYSTEM', 'KI_ASSISTENT_FRAGE');
  return antwort;
}

/** Fasst die administrative Firmen-Akte zusammen und schlägt nächste Schritte vor. */
export async function summarizeAkte(mandantId: MandantID, mandantName: string): Promise<string> {
  const eintraege = await loadAkte(mandantId);
  if (eintraege.length === 0) {
    return `Für ${mandantName} sind noch keine Akten-Einträge vorhanden.`;
  }

  const kontext = eintraege
    .slice(0, 40)
    .map(e => {
      const datum = e.datum.slice(0, 10);
      const typ = e.typ;
      const kopf = `[${datum} · ${typ}] ${e.titel}`;
      const inhalt = e.inhalt !== '' ? `\n${e.inhalt.slice(0, 800)}` : '';
      return kopf + inhalt;
    })
    .join('\n\n---\n\n');

  const prompt = `Hier ist die administrative Firmen-Akte von "${mandantName}" (Besprechungsnotizen, E-Mails, Belege, Dokumente). Fasse den Stand kompakt zusammen: (1) Was ist der aktuelle Stand / die wichtigsten Themen? (2) Welche offenen Punkte / nächsten Schritte ergeben sich? Nutze Aufzählungen.

AKTE:
${kontext}`;

  const antwort = await aiChat(SYSTEM_PROMPT, [{ role: 'user', content: prompt }]);
  void logAktion(mandantName, 'KI_AKTE_ZUSAMMENFASSUNG', { eintraege: eintraege.length });
  return antwort;
}

export function isAssistantAvailable(): boolean {
  return isAiConfigured();
}
